import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Card, Wallet } from '@asia/shared';
import { haptic } from '../../hooks/useTelegram';
import { useWalletBalances } from '../../hooks/useChain';
import { useWallets } from '../../store/wallets';
import { groupInt, fmtUsd } from '../../lib/format';
import { topupQuoteApi, type TopupQuote } from '../../lib/cards';
import { feeQuoteApi, type FeeQuote } from '../../lib/transfers';
import { CoinIcon } from '../CoinIcon';
import { FigIcon } from '../FigIcon';
import { ChevronRight } from '../icons';
import { Sheet } from './Sheet';
import { CardTile } from './CardSheets';

/** USDT label for a wallet row in the picker (own query so every row is live). */
function WalletUsdtLabel({ wallet }: { wallet: Wallet }) {
  const { usdt } = useWalletBalances(wallet);
  return (
    <span className="text-[13px] text-muted">{groupInt(usdt)} USDT</span>
  );
}

/** Top up a card with USDT from a chosen wallet. */
export function TopUpSheet({
  card,
  onConfirm,
  onClose,
}: {
  card: Card;
  onConfirm: (amount: string, walletId: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const wallets = useWallets((s) => s.wallets);

  const activeId = useWallets((s) => s.activeId);
  const [walletId, setWalletId] = useState(activeId ?? wallets[0]?.id ?? '');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [quote, setQuote] = useState<TopupQuote | null>(null);
  const [networkQuote, setNetworkQuote] = useState<FeeQuote | null>(null);
  const [quoteFailed, setQuoteFailed] = useState(false);

  const wallet = wallets.find((w) => w.id === walletId) ?? wallets[0]!;
  const available = useWalletBalances(wallet).usdt;
  const amountNum = useMemo(() => Number(amount.replace(',', '.')) || 0, [amount]);

  // Limits come from a no-amount quote; fees re-quote with the typed amount.
  useEffect(() => {
    let alive = true;
    topupQuoteApi(card.id).then(
      (q) => alive && setQuote((prev) => prev ?? q),
      () => alive && setQuoteFailed(true),
    );
    return () => {
      alive = false;
    };
  }, [card.id]);

  useEffect(() => {
    let alive = true;
    const handle = window.setTimeout(() => {
      topupQuoteApi(card.id, amountNum > 0 ? String(amountNum) : undefined).then(
        (q) => {
          if (!alive) return;
          setQuote(q);
          setQuoteFailed(false);
        },
        () => alive && setQuoteFailed(true),
      );
    }, 200);
    return () => {
      alive = false;
      window.clearTimeout(handle);
    };
  }, [card.id, amountNum]);

  useEffect(() => {
    let alive = true;
    setNetworkQuote(null);
    if (!quote?.payTo) return () => { alive = false; };
    const handle = window.setTimeout(() => {
      feeQuoteApi('USDT', quote.payTo, wallet.address, amountNum > 0 ? String(amountNum) : undefined, {
        kind: 'topup',
        cardId: card.id,
        ...(amountNum > 0 ? { amount: amountNum } : {}),
      }).then(
        (q) => alive && setNetworkQuote(q),
        () => alive && setQuoteFailed(true),
      );
    }, 200);
    return () => {
      alive = false;
      window.clearTimeout(handle);
    };
  }, [card.id, quote?.payTo, wallet.address, amountNum]);

  const networkFee = networkQuote?.feeUsd ?? 0;
  const fullNetworkFee = networkQuote?.fullFeeUsd ?? networkFee;
  const networkDiscount = networkQuote?.subsidyUsd ?? 0;
  const topUpFee = quote?.topupFeeUsd ?? (quote?.projectFeeUsd ?? 0) + (quote?.providerFeeUsd ?? 0);
  const topUpFeePct = quote?.topupFeePct ?? 3;
  const fees = topUpFee + networkFee;
  const total = amountNum > 0 ? Math.round((amountNum + fees) * 100) / 100 : 0;
  const min = quote?.minTopupUsd ?? null;
  const max = quote?.maxTopupUsd ?? null;
  const fromWallet = quote?.paymentRequired ?? true;

  // Amount at which the % network budget covers the (post-warm) fee entirely.
  const freeFrom = useMemo(() => {
    const fromApi = networkQuote?.freeFromUsd;
    if (fromApi != null && fromApi > 0) return fromApi;
    const pct = networkQuote?.subsidyPct ?? 0;
    const full = networkQuote?.fullFeeUsd ?? 0;
    const warm = networkQuote?.warmCoveredUsd ?? 0;
    const payable = Math.max(0, full - warm);
    if (!(pct > 0) || !(payable > 0)) return null;
    return Math.ceil(payable / (pct / 100));
  }, [networkQuote]);

  const pickFreeAmount = freeFrom != null
    ? Math.max(freeFrom, min ?? 0)
    : null;
  const canReachFree = (() => {
    if (pickFreeAmount == null) return false;
    if (max != null && pickFreeAmount > max) return false;
    if (!fromWallet) return true;
    const feeAtFree = Math.round((pickFreeAmount * topUpFeePct) / 100 * 100) / 100;
    return pickFreeAmount + feeAtFree <= available + 1e-9;
  })();

  const over = fromWallet && total > available;
  const belowMin = min != null && amountNum > 0 && amountNum < min;
  const aboveMax = max != null && amountNum > max;
  const canConfirm = !!quote && !!networkQuote && amountNum > 0 && !over && !belowMin && !aboveMax;
  const usdtLabel = `${groupInt(amountNum)} USDT`;

  const onAmount = (v: string) => {
    const cleaned = v
      .replace(/[^\d.,]/g, '')
      .replace(/[.,]/, '§')
      .replace(/[.,]/g, '')
      .replace('§', '.');
    setAmount(cleaned);
  };
  const setMax = () => {
    haptic();
    const spendable = Math.max(0, Math.floor((available - fees) * 100) / 100);
    setAmount(String(max != null ? Math.min(spendable, max) : spendable));
  };
  const pickFree = () => {
    if (pickFreeAmount == null) return;
    haptic();
    setAmount(String(pickFreeAmount));
  };

  const error = quoteFailed
    ? t('cards.unavailable')
    : over
      ? t('cards.insufficient')
      : belowMin
        ? t('cards.belowMin', { amount: fmtUsd(min ?? 0) })
        : aboveMax
          ? t('cards.aboveMax', { amount: fmtUsd(max ?? 0) })
          : null;

  const topUpFeeLabel =
    amountNum > 0
      ? `${t('cards.feeTopUp')} (${topUpFeePct}%)`
      : t('cards.feeTopUp');

  return (
    <Sheet
      onClose={onClose}
      panelClassName="flex w-full flex-col items-center gap-6 rounded-t-[36px] bg-surface px-4 pb-[44px] pt-4"
    >
      {(close) => (
        <>
          <div className="flex flex-col items-center gap-[6px]">
            <p className="text-[16px] font-medium text-ink">{t('cards.topUpTitle')}</p>
            <p className="text-[14px] text-muted">{t('cards.topUpFrom')}</p>
          </div>

          <CardTile card={card} />

          {fromWallet && (
          <div className="relative w-full">
            <p className="mb-3 px-4 text-[15px] font-medium text-muted">
              {t('cards.fromWallet')}
            </p>
            <button
              onClick={() => {
                haptic();
                setPickerOpen((o) => !o);
              }}
              className="flex w-full items-center gap-3 rounded-[26px] bg-card p-4 active:opacity-80"
            >
              <CoinIcon symbol="TRX" size={40} />
              <span className="flex min-w-0 flex-1 flex-col items-start">
                <span className="truncate text-[15px] font-medium text-ink">{wallet.name}</span>
                <span className="text-[13px] text-muted">{groupInt(available)} USDT</span>
              </span>
              <ChevronRight width={18} height={18} className="rotate-90 text-subtle" />
            </button>

            {pickerOpen && (
              <>
                <button
                  aria-label={t('common.close')}
                  className="fixed inset-0 z-40 cursor-default"
                  onClick={() => setPickerOpen(false)}
                />
                <div
                  className="fade-in absolute inset-x-0 top-[calc(100%+8px)] z-50 flex flex-col gap-3 rounded-[28px] p-4 backdrop-blur-[8px]"
                  style={{
                    backgroundColor: 'rgb(var(--sheet) / 0.9)',
                    boxShadow: '0px 0px 40px 0px rgba(0,0,0,0.12)',
                  }}
                >
                  {wallets.map((w, i) => (
                    <div key={w.id} className="flex flex-col gap-3">
                      {i > 0 && <div className="h-px w-full bg-press/[0.08]" />}
                      <button
                        onClick={() => {
                          haptic();
                          setWalletId(w.id);
                          setPickerOpen(false);
                        }}
                        className="flex items-center gap-3 active:opacity-70"
                      >
                        <CoinIcon symbol="TRX" size={40} />
                        <span className="flex min-w-0 flex-1 flex-col items-start">
                          <span className="truncate text-[15px] font-medium text-ink">
                            {w.name}
                          </span>
                          <WalletUsdtLabel wallet={w} />
                        </span>
                        {w.id === walletId && (
                          <FigIcon name="pfCheck" size={20} className="text-accent" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          )}

          <div className="flex w-full flex-col gap-3">
            <div className="flex items-center px-4 text-[15px] font-medium">
              <p className="flex-1 text-muted">{t('send.amount')}</p>
              {fromWallet && (
                <button onClick={setMax} className="text-accent active:opacity-70">
                  <span className="text-muted">{t('send.max')}</span> {groupInt(available)} USDT
                </button>
              )}
            </div>
            <div className="flex h-[60px] items-center justify-between gap-2 rounded-[26px] bg-card py-4 pl-4 pr-3">
              <input
                value={amount}
                onChange={(e) => onAmount(e.target.value)}
                inputMode="decimal"
                placeholder="0"
                className="[font-family:'SF_Pro_Rounded',system-ui] min-w-0 flex-1 bg-transparent text-[24px] font-medium text-ink outline-none placeholder:text-muted"
              />
              <span className="flex shrink-0 items-center gap-1 rounded-full bg-surface p-[6px]">
                <CoinIcon symbol="USDT" size={24} />
                <span className="text-[15px] font-medium text-ink">USDT</span>
              </span>
            </div>
            {min != null && !error && (
              <p className="px-4 text-[13px] text-muted">
                {t('cards.minTopUpHint', { amount: fmtUsd(min) })}
              </p>
            )}
            {error && <p className="px-4 text-[13px] font-medium text-negative">{error}</p>}
          </div>

          <div className="flex w-full flex-col gap-3 rounded-[24px] bg-card p-4">
            {quote && networkQuote ? (
              <>
                <Row label={topUpFeeLabel} value={fmtUsd(topUpFee)} />
                <div className="h-px w-full bg-hairline" />
                <Row
                  label={t('send.networkFee')}
                  value={
                    networkFee <= 0 && fullNetworkFee > 0 ? (
                      <span className="inline-flex items-baseline gap-2">
                        <span className="text-faint line-through">{fmtUsd(fullNetworkFee)}</span>
                        <span className="text-accent">{t('cards.networkFree')}</span>
                      </span>
                    ) : networkDiscount > 0 ? (
                      <span className="inline-flex items-baseline gap-2">
                        <span className="text-faint line-through">{fmtUsd(fullNetworkFee)}</span>
                        <span>{fmtUsd(networkFee)}</span>
                      </span>
                    ) : (
                      fmtUsd(fullNetworkFee)
                    )
                  }
                />
                {networkDiscount > 0 && networkFee > 0 && (
                  <>
                    <div className="h-px w-full bg-hairline" />
                    <Row label={t('cards.networkDiscount')} value={fmtUsd(-networkDiscount)} />
                  </>
                )}
                <div className="h-px w-full bg-hairline" />
                {fromWallet ? (
                  <Row label={t('cards.youPay')} value={`${fmtUsd(total)} USDT`} strong />
                ) : (
                  <Row label={t('cards.cardReceives')} value={`${fmtUsd(amountNum)} USDT`} strong />
                )}
              </>
            ) : (
              <div className="flex h-[76px] items-center justify-center">
                <span className="size-6 animate-spin rounded-full border-[3px] border-accent/25 border-t-accent" />
              </div>
            )}
          </div>

          {quote && networkQuote && pickFreeAmount != null && canReachFree && networkFee > 0 && (
            <button
              type="button"
              onClick={pickFree}
              className="flex w-full items-center gap-3 rounded-[24px] bg-accent/[0.08] px-4 py-3.5 text-left transition-colors active:bg-accent/[0.14]"
            >
              <span className="grid size-10 shrink-0 place-items-center rounded-full bg-accent/15 text-accent">
                <FigIcon name="pfCheck" size={18} />
              </span>
              <span className="min-w-0 flex-1 text-[14px] font-semibold leading-snug text-accent">
                {amountNum > 0 && amountNum < pickFreeAmount
                  ? t('cards.netToFreeCta', {
                      amount: fmtUsd(Math.max(0, pickFreeAmount - amountNum)),
                    })
                  : t('cards.netFreeCta', { amount: fmtUsd(pickFreeAmount) })}
              </span>
              <ChevronRight width={16} height={16} className="shrink-0 text-accent/60" />
            </button>
          )}

          <button
            onClick={() => {
              haptic('medium');
              onConfirm(String(amountNum), wallet.id);
              close();
            }}
            disabled={!canConfirm}
            className={`h-[56px] w-full rounded-full border border-card text-[15px] font-bold transition-colors ${
              canConfirm ? 'bg-accent text-white active:bg-accent-ink' : 'bg-accent text-white/[0.44]'
            }`}
          >
            {t('cards.topUpCta', { amount: usdtLabel })}
          </button>
        </>
      )}
    </Sheet>
  );
}

function Row({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: ReactNode;
  strong?: boolean;
}) {
  return (
    <div className="flex w-full items-center justify-between">
      <span className="text-[15px] text-faint">{label}</span>
      <span className={`text-right text-[15px] text-ink ${strong ? 'font-bold' : 'font-medium'}`}>
        {value}
      </span>
    </div>
  );
}
